import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { BUY_MAX_PHOTOS, type BuyRequestView } from "../../shared/buy";
import { formatArs } from "../../shared/money";
import { CONDITION_LABEL } from "../../shared/variants";
import { useAuth } from "../auth/AuthProvider";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { useI18n } from "../i18n/I18nProvider";
import { ApiError, imageUrl } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { buyApi } from "./api";

/** /cuenta/compramos — the cards someone offered the store, and the store's offers. */
export function MyBuyRequests() {
  const { t } = useI18n();
  const auth = useAuth();
  const [params] = useSearchParams();
  const list = useAsync((signal) => buyApi.mine(signal), []);
  if (!auth.loading && !auth.user) return <Navigate to="/ingresar?next=%2Fcuenta%2Fcompramos" replace />;
  if (list.error) return <ErrorMessage onRetry={list.reload} />;
  if (list.loading || !list.data) return <Loading />;
  return (
    <div className="container section stack buy-mine">
      <p className="breadcrumbs">
        <Link to="/cuenta">{t("account.title")}</Link> › {t("buy.mine")}
      </p>
      <div className="section-head">
        <h1>{t("buy.mine")}</h1>
        <Link to="/compramos" className="btn btn-primary">
          {t("buy.offerNew")}
        </Link>
      </div>
      {params.get("error") === "photos" && <p className="notice notice-warn">{t("buy.errorPhotoUpload")}</p>}
      {list.data.length === 0 && <p className="muted">{t("buy.mineEmpty")}</p>}
      {list.data.map((r) => (
        <Request key={r.number} r={r} onChange={list.reload} />
      ))}
    </div>
  );
}

function Request({ r, onChange }: { r: BuyRequestView; onChange: () => void }) {
  const { t, locale } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState({ holder: "", cbu: "", alias: "" });
  const units = r.items.reduce((n, i) => n + i.quantity, 0);

  const run = async (work: () => Promise<unknown>, onError = t("error.generic")) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      onChange();
    } catch (err) {
      setError(err instanceof ApiError && err.code === "payout_account" ? t("buy.accountError") : onError);
    } finally {
      setBusy(false);
    }
  };
  const upload = (files: FileList | null) =>
    run(async () => {
      for (const f of [...(files ?? [])].slice(0, BUY_MAX_PHOTOS - r.photos.length)) await buyApi.uploadPhoto(r.number, f);
    }, t("buy.errorPhotoUpload"));

  return (
    <article className="stack-sm buy-request" aria-label={r.number}>
      <h2 className="small-title">
        {r.number} · <span className="pill">{t(`buy.status.${r.status}`)}</span>
      </h2>
      <p className="muted small">
        {t("buy.itemsCount", { count: units })}
        {r.submittedAt ? ` · ${t("buy.sentOn", { date: new Date(r.submittedAt).toLocaleDateString("es-AR") })}` : ""}
      </p>
      <ul className="small">
        {r.items.map((i) => (
          <li key={i.id}>
            {i.quantity} × {i.name} ({CONDITION_LABEL[i.condition][locale]})
          </li>
        ))}
      </ul>
      {r.photos.length > 0 && (
        <div className="buy-photos">
          {r.photos.map((p) => (
            <img key={p} src={imageUrl(p)} alt="" width={72} height={72} loading="lazy" />
          ))}
        </div>
      )}
      {r.status === "draft" && (
        <>
          <p className="notice notice-warn">{t("buy.draftHint")}</p>
          {r.photos.length < BUY_MAX_PHOTOS && (
            <label className="btn btn-outline btn-sm">
              {t("buy.addPhotos")}
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden disabled={busy} onChange={(e) => void upload(e.target.files)} />
            </label>
          )}
        </>
      )}
      {r.offerArs !== null && (r.status === "quoted" || r.status === "accepted" || r.status === "rejected" || r.status === "completed") && (
        <div className="buy-offer">
          <p>
            <strong>{t("buy.offer", { amount: formatArs(r.offerArs) })}</strong>
          </p>
          {r.offerNote && <p>{r.offerNote}</p>}
          {r.status === "quoted" && <p className="muted small">{t("buy.offerTerms")}</p>}
        </div>
      )}
      {r.status === "accepted" && <p>{t("buy.acceptedHint", { phone: r.contactPhone })}</p>}
      {r.reason && (r.status === "declined" || r.status === "cancelled") && <p>{t("buy.reason", { reason: r.reason })}</p>}

      {r.actions.includes("accept") && r.payout === "transfer" && (
        <fieldset className="stack-sm">
          <legend>{t("buy.accountTitle")}</legend>
          <input aria-label={t("buy.holder")} placeholder={t("buy.holder")} value={account.holder} maxLength={120} onChange={(e) => setAccount({ ...account, holder: e.target.value })} />
          <input aria-label="CBU / CVU" placeholder="CBU / CVU" inputMode="numeric" value={account.cbu} maxLength={30} onChange={(e) => setAccount({ ...account, cbu: e.target.value })} />
          <input aria-label="Alias" placeholder="Alias" value={account.alias} maxLength={40} onChange={(e) => setAccount({ ...account, alias: e.target.value })} />
        </fieldset>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div className="row-actions">
        {r.actions.includes("submit") && (
          <button type="button" className="btn btn-primary btn-sm" disabled={busy || r.photos.length === 0} onClick={() => void run(() => buyApi.act(r.number, "send"))}>
            {t("buy.send")}
          </button>
        )}
        {r.actions.includes("accept") && (
          <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void run(() => buyApi.act(r.number, "accept", r.payout === "transfer" ? account : undefined))}>
            {t("buy.accept")}
          </button>
        )}
        {r.actions.includes("reject") && (
          <button type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={() => void run(() => buyApi.act(r.number, "reject"))}>
            {t("buy.reject")}
          </button>
        )}
        {r.actions.includes("cancel") && (
          <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => window.confirm(t("buy.cancelConfirm")) && void run(() => buyApi.act(r.number, "cancel"))}>
            {t("buy.cancel")}
          </button>
        )}
      </div>
    </article>
  );
}
